import { createContext, useContext, useState } from 'react'
import type { ReactNode } from 'react'
import type { Product } from '@/shared/types'

export type OrderLine = {
  product: Product
  qty: number
  sellAsPack: boolean
}

const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

export function generateOrderCode(): string {
  return Array.from(
    { length: 6 },
    () => CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]
  ).join('')
}

type OrderContextValue = {
  lines: OrderLine[]
  setLines: React.Dispatch<React.SetStateAction<OrderLine[]>>
  orderCode: string
  paidRaw: string
  setPaidRaw: React.Dispatch<React.SetStateAction<string>>
  discountRaw: string
  setDiscountRaw: React.Dispatch<React.SetStateAction<string>>
  discountMode: 'flat' | 'pct'
  setDiscountMode: React.Dispatch<React.SetStateAction<'flat' | 'pct'>>
  resetOrder: () => void
}

const OrderContext = createContext<OrderContextValue | null>(null)

export function OrderProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<OrderLine[]>([])
  const [orderCode, setOrderCode] = useState(generateOrderCode)
  const [paidRaw, setPaidRaw] = useState('')
  const [discountRaw, setDiscountRaw] = useState('')
  const [discountMode, setDiscountMode] = useState<'flat' | 'pct'>('flat')

  function resetOrder() {
    setLines([])
    setPaidRaw('')
    setDiscountRaw('')
    setDiscountMode('flat')
    setOrderCode(generateOrderCode())
  }

  return (
    <OrderContext.Provider value={{ lines, setLines, orderCode, paidRaw, setPaidRaw, discountRaw, setDiscountRaw, discountMode, setDiscountMode, resetOrder }}>
      {children}
    </OrderContext.Provider>
  )
}

export function useOrderContext() {
  const ctx = useContext(OrderContext)
  if (!ctx) throw new Error('useOrderContext must be used within OrderProvider')
  return ctx
}
